import styles from './Gallery.module.scss'
import { TrophyGallery } from '~/components/TrophyGallery/TrophyGallery'
import { ChangeEventHandler, ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { OrganizerFilterComponentDto } from '~/modules/Organizers/Infrastructure/OrganizerFilterComponentDto'
import { OrganizerFilter } from '~/modules/Trophies/Infrastructure/Components/OrganizerFilter'
import { useRouter } from 'next/router'
import { ComponentTrophyDto } from '~/modules/Products/Infrastructure/Dtos/ComponentTrophyDto'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'
import { UiAppearance } from '~/modules/UiVariant/appearance'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDownWideShort, faChevronDown, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import {
  DEFAULT_TROPHY_MEMORIES_FILTER,
  normalizeTrophyDisciplineOption,
  TrophyDisciplineFilterOption,
  TrophyMemoriesFilter,
} from '~/modules/Trophies/Application/TrophyListFilters'
import {
  galleryMemoriesFilterOptions,
  useGalleryToolbarState,
} from '~/components/Gallery/useGalleryToolbarState'

interface Props {
  trophies: ComponentTrophyDto[]
  organizers: OrganizerFilterComponentDto[]
  organizerId: string | null
  minMemories?: TrophyMemoriesFilter
  disciplineOptions?: TrophyDisciplineFilterOption[]
  discipline?: string | null
  positionOptions?: string[]
  position?: string | null
  appearance?: UiAppearance
  showTitle?: boolean
  showTrophiesTitle?: boolean
  trophyAppearance?: 'default' | 'gallery' | 'museumGallery'
}

type GalleryErrorScope = 'refresh' | 'pagination'

interface GalleryRequestError {
  pageNumber: number
  organizerId: string | null
  minMemories: TrophyMemoriesFilter
  discipline: string | null
  position: string | null
  scope: GalleryErrorScope
}

export const Gallery: FC<Props> = ({
  trophies: initialTrophies,
  organizers,
  organizerId: initialOrganizerId,
  minMemories: initialMinMemories = DEFAULT_TROPHY_MEMORIES_FILTER,
  disciplineOptions: initialDisciplineOptions = [],
  discipline: initialDiscipline = null,
  positionOptions: initialPositionOptions = [],
  position: initialPosition = null,
  appearance = 'default',
  showTitle = true,
  showTrophiesTitle = true,
  trophyAppearance = 'default',
}) => {
  const { t } = useTranslation('gallery')
  const pageSize = 12
  const [trophies, setTrophies] = useState(initialTrophies)
  const [loadingTrophies, setLoadingTrophies] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMoreTrophies, setHasMoreTrophies] = useState(initialTrophies.length >= pageSize)
  const [organizerId, setOrganizerId] = useState<string | null>(initialOrganizerId)
  const [discipline, setDiscipline] = useState<string | null>(initialDiscipline)
  const [position, setPosition] = useState<string | null>(initialPosition)
  const [requestError, setRequestError] = useState<GalleryRequestError | null>(null)
  const router = useRouter()
  const isMuseumHome = appearance === 'museumHome'
  const {
    activeMemoriesFilter,
    searchTerm,
    setActiveMemoriesFilter,
    setSearchTerm,
    setSortMode,
    sortMode,
    visibleTrophies,
  } = useGalleryToolbarState({
    trophies,
    minMemories: initialMinMemories,
  })

  useEffect(() => {
    setTrophies(initialTrophies)
    setHasMoreTrophies(initialTrophies.length >= pageSize)
    setOrganizerId(initialOrganizerId)
    setDiscipline(initialDiscipline)
    setPosition(initialPosition)
    setPageNumber(1)
    setRequestError(null)
  }, [initialDiscipline, initialOrganizerId, initialPosition, initialTrophies])

  const fetchTrophiesPage = async (
    currentPage: number,
    currentOrganizerId: string | null,
    currentMinMemories: TrophyMemoriesFilter,
    currentDiscipline: string | null,
    currentPosition: string | null
  ) => {
    const params = new URLSearchParams()

    params.append('page[number]', currentPage.toString())
    params.append('page[size]', pageSize.toString())

    if (currentOrganizerId !== null) {
      params.append('organizerId', currentOrganizerId)
    }

    if (currentMinMemories > DEFAULT_TROPHY_MEMORIES_FILTER) {
      params.append('minMemories', currentMinMemories.toString())
    }

    if (currentDiscipline !== null) {
      params.append('discipline', currentDiscipline)
    }

    if (currentPosition !== null) {
      params.append('position', currentPosition)
    }

    const response = await fetch(`/api/trophies?${params}`)

    if (!response.ok) {
      throw new Error(`Failed to load trophies: ${response.status}`)
    }

    const apiTrophies: ApplicationTrophyDto[] = await response.json()

    return apiTrophies.map((trophy) => ComponentTrophyDtoTransformer.fromApplication(trophy))
  }

  const pushGalleryQuery = useCallback(async (
    currentOrganizerId: string | null,
    currentMinMemories: TrophyMemoriesFilter,
    currentDiscipline: string | null,
    currentPosition: string | null
  ) => {
    const query: Record<string, string> = {}

    if (currentOrganizerId !== null) {
      query.organizerId = currentOrganizerId
    }

    if (currentMinMemories > DEFAULT_TROPHY_MEMORIES_FILTER) {
      query.minMemories = currentMinMemories.toString()
    }

    if (currentDiscipline !== null) {
      query.discipline = currentDiscipline
    }

    if (currentPosition !== null) {
      query.position = currentPosition
    }

    await router.push({
      pathname: router.pathname,
      query,
    })
  }, [router])

  const loadMoreTrophies = useCallback(async () => {
    if (loadingTrophies || !hasMoreTrophies) {
      return
    }

    setLoadingTrophies(true)
    setRequestError(null)
    try {
      const currentPage = pageNumber + 1
      const newTrophies = await fetchTrophiesPage(
        currentPage,
        organizerId,
        activeMemoriesFilter,
        discipline,
        position
      )

      setPageNumber(currentPage)
      setHasMoreTrophies(newTrophies.length >= pageSize)

      if (newTrophies.length > 0) {
        setTrophies((previousTrophies) => [...previousTrophies, ...newTrophies])
      }
    } catch (exception: unknown) {
      console.error(exception)
      setRequestError({
        scope: 'pagination',
        organizerId,
        minMemories: activeMemoriesFilter,
        discipline,
        position,
        pageNumber: pageNumber + 1,
      })
    } finally {
      setLoadingTrophies(false)
    }
  }, [activeMemoriesFilter, discipline, hasMoreTrophies, loadingTrophies, organizerId, pageNumber, position])

  const fetchTrophies = async (
    currentOrganizerId: string | null,
    currentMinMemories: TrophyMemoriesFilter,
    currentDiscipline: string | null,
    currentPosition: string | null
  ) => {
    setLoadingTrophies(true)
    setRequestError(null)
    setOrganizerId(currentOrganizerId)
    setDiscipline(currentDiscipline)
    setPosition(currentPosition)
    setActiveMemoriesFilter(currentMinMemories)
    try {
      const currentPage = 1
      const trophies = await fetchTrophiesPage(
        currentPage,
        currentOrganizerId,
        currentMinMemories,
        currentDiscipline,
        currentPosition
      )

      setPageNumber(currentPage)
      setHasMoreTrophies(trophies.length >= pageSize)
      setTrophies(() => trophies)
      await pushGalleryQuery(currentOrganizerId, currentMinMemories, currentDiscipline, currentPosition)
    } catch (exception: unknown) {
      console.error(exception)
      setRequestError({
        scope: 'refresh',
        organizerId: currentOrganizerId,
        minMemories: currentMinMemories,
        discipline: currentDiscipline,
        position: currentPosition,
        pageNumber: 1,
      })
    } finally {
      setLoadingTrophies(false)
    }
  }

  useEffect(() => {
    const normalizedSearch = searchTerm.trim()

    if (!isMuseumHome || normalizedSearch.length === 0) {
      return
    }

    if (visibleTrophies.length > 0 || loadingTrophies || !hasMoreTrophies) {
      return
    }

    if (requestError?.scope === 'pagination') {
      return
    }

    void loadMoreTrophies()
  }, [
    loadMoreTrophies,
    hasMoreTrophies,
    isMuseumHome,
    loadingTrophies,
    requestError?.scope,
    searchTerm,
    visibleTrophies.length,
  ])

  const handleOrganizerFilterChange: ChangeEventHandler<HTMLSelectElement> =
    async (ev: ChangeEvent<HTMLSelectElement>) => {
      let currentOrganizerId: string | null = null

      if (ev.target.value.length > 0) {
        currentOrganizerId = ev.target.value
      }

      if (currentOrganizerId === organizerId) {
        return
      }

      await fetchTrophies(currentOrganizerId, activeMemoriesFilter, discipline, position)
    }

  const handleMemoriesFilterChange = useCallback(async (nextMinMemories: TrophyMemoriesFilter) => {
    if (nextMinMemories === activeMemoriesFilter) {
      return
    }

    await fetchTrophies(organizerId, nextMinMemories, discipline, position)
  }, [activeMemoriesFilter, discipline, organizerId, position])

  const handleDisciplineFilterChange: ChangeEventHandler<HTMLSelectElement> =
    async (ev: ChangeEvent<HTMLSelectElement>) => {
      const nextDiscipline = ev.target.value.trim().length > 0 ? ev.target.value : null

      if (nextDiscipline === discipline) {
        return
      }

      await fetchTrophies(organizerId, activeMemoriesFilter, nextDiscipline, position)
    }

  const handlePositionFilterChange: ChangeEventHandler<HTMLSelectElement> =
    async (ev: ChangeEvent<HTMLSelectElement>) => {
      const nextPosition = ev.target.value.trim().length > 0 ? ev.target.value : null

      if (nextPosition === position) {
        return
      }

      await fetchTrophies(organizerId, activeMemoriesFilter, discipline, nextPosition)
    }

  const retryFailedRequest = useCallback(async () => {
    if (requestError === null) {
      return
    }

    if (requestError.scope === 'pagination') {
      setLoadingTrophies(true)
      setRequestError(null)

      try {
        const newTrophies = await fetchTrophiesPage(
          requestError.pageNumber,
          requestError.organizerId,
          requestError.minMemories,
          requestError.discipline,
          requestError.position
        )

        setPageNumber(requestError.pageNumber)
        setHasMoreTrophies(newTrophies.length >= pageSize)

        if (newTrophies.length > 0) {
          setTrophies((previousTrophies) => [...previousTrophies, ...newTrophies])
        }
      } catch (exception: unknown) {
        console.error(exception)
        setRequestError(requestError)
      } finally {
        setLoadingTrophies(false)
      }

      return
    }

    await fetchTrophies(
      requestError.organizerId,
      requestError.minMemories,
      requestError.discipline,
      requestError.position
    )
  }, [requestError])

  const emptyState = useMemo(() => {
    const normalizedSearch = searchTerm.trim()

    if (normalizedSearch.length > 0) {
      return {
        description: t(
          'empty_results_description',
          { defaultValue: 'Try another search term, organizer, discipline, or position filter.' }
        ),
        title: t('empty_results_title', { defaultValue: 'No matching items' }),
      }
    }

    if (discipline !== null) {
      return {
        description: t(
          'empty_discipline_description',
          {
            defaultValue: 'Try another discipline or clear the discipline filter.',
            discipline,
          }
        ),
        title: t('empty_results_title', { defaultValue: 'No matching items' }),
      }
    }

    if (position !== null) {
      return {
        description: t(
          'empty_position_description',
          {
            defaultValue: 'Try another position or clear the position filter.',
            position,
          }
        ),
        title: t('empty_results_title', { defaultValue: 'No matching items' }),
      }
    }

    if (activeMemoriesFilter === 2) {
      return {
        description: t(
          'empty_multiple_memories_description',
          { defaultValue: 'Try widening the memories filter without clearing your organizer or search.' }
        ),
        title: t(
          'empty_multiple_memories_title',
          { defaultValue: 'No trophies with 2+ memories in this selection' }
        ),
      }
    }

    if (activeMemoriesFilter === 1) {
      return {
        description: t(
          'empty_memories_description',
          { defaultValue: 'Try viewing all trophies in the current selection.' }
        ),
        title: t(
          'empty_memories_title',
          { defaultValue: 'No trophies with memories in this selection' }
        ),
      }
    }

    return {
      description: t(
        'empty_results_description',
        { defaultValue: 'Try another search term, organizer, discipline, or position filter.' }
      ),
      title: t('empty_results_title', { defaultValue: 'No matching items' }),
    }
  }, [activeMemoriesFilter, discipline, position, searchTerm, t])

  const organizersLabel = t(
    'organizers_label',
    { defaultValue: organizers.length === 1 ? 'ORGANIZER' : 'ORGANIZERS' }
  )
  const itemsLabel = t(
    'items_label',
    { defaultValue: visibleTrophies.length === 1 ? 'ITEM' : 'ITEMS' }
  )
  const sortLabel = sortMode === 'recent'
    ? t('sort_recent', { defaultValue: 'Most recent' })
    : t('sort_alphabetical', { defaultValue: 'A-Z' })
  const showEmptyStateActions = searchTerm.trim().length === 0 && activeMemoriesFilter > 0
  const showLoadMoreButton = hasMoreTrophies || loadingTrophies
  const showGalleryContent = requestError?.scope !== 'refresh'
  const disciplineOptions = useMemo(() => {
    const options = new Map<string, TrophyDisciplineFilterOption>()

    initialDisciplineOptions.forEach((disciplineOption) => {
      if (disciplineOption.key.trim().length > 0 && disciplineOption.label.trim().length > 0) {
        options.set(disciplineOption.key, disciplineOption)
      }
    })

    if (discipline !== null && !options.has(discipline)) {
      const normalizedOption = normalizeTrophyDisciplineOption(discipline)
      options.set(discipline, normalizedOption ?? { key: discipline, label: discipline })
    }

    return Array.from(options.values()).sort((left, right) => left.label.localeCompare(right.label))
  }, [discipline, initialDisciplineOptions])
  const positionOptions = useMemo(() => {
    const options = new Set(
      initialPositionOptions.filter((positionOption) => positionOption.trim().length > 0)
    )

    if (position !== null) {
      options.add(position)
    }

    return Array.from(options).sort((left, right) => left.localeCompare(right))
  }, [initialPositionOptions, position])

  return (
    <section
      className={ `
        ${styles.gallery__container}
        ${isMuseumHome ? styles.museumHome : ''}
      ` }
    >
      { showTitle && (
        <h1 className={ styles.gallery__title }>
          { t('title') }
        </h1>
      ) }

      {
        isMuseumHome
          ? <div className={ styles.gallery__toolbar }>
              <div className={ styles.gallery__toolbarControls }>
                <OrganizerFilter
                  organizers={ organizers }
                  organizerId={ organizerId }
                  setOrganizerId={ handleOrganizerFilterChange }
                  appearance={ appearance }
                />

                <label className={ styles.gallery__search }>
                  <span className={ styles.gallery__searchIcon }>
                    <FontAwesomeIcon icon={ faMagnifyingGlass } />
                  </span>

                  <input
                    type="search"
                    value={ searchTerm }
                    className={ styles.gallery__searchInput }
                    placeholder={ t(
                      'search_placeholder',
                      { defaultValue: 'Search by sport, competition or athlete' }
                    ) }
                    aria-label={ t(
                      'search_label',
                      { defaultValue: 'Search by sport, competition or athlete' }
                    ) }
                    onChange={ (event) => setSearchTerm(event.target.value) }
                  />
                </label>

                <label className={ styles.gallery__disciplineFilter }>
                  <span className={ styles.gallery__disciplineFilterLabel }>
                    { t('discipline_filter_label', { defaultValue: 'Discipline' }) }
                  </span>
                  <select
                    className={ styles.gallery__disciplineFilterSelect }
                    value={ discipline ?? '' }
                    onChange={ handleDisciplineFilterChange }
                    aria-label={ t('discipline_filter_label', { defaultValue: 'Discipline' }) }
                  >
                    <option value="">
                      { t('discipline_filter_all', { defaultValue: 'All disciplines' }) }
                    </option>
                    {
                      disciplineOptions.map((disciplineOption) => (
                        <option key={ disciplineOption.key } value={ disciplineOption.key }>
                          { disciplineOption.label }
                        </option>
                      ))
                    }
                  </select>
                </label>

                <label className={ styles.gallery__positionFilter }>
                  <span className={ styles.gallery__positionFilterLabel }>
                    { t('position_filter_label', { defaultValue: 'Position' }) }
                  </span>
                  <select
                    className={ styles.gallery__positionFilterSelect }
                    value={ position ?? '' }
                    onChange={ handlePositionFilterChange }
                    aria-label={ t('position_filter_label', { defaultValue: 'Position' }) }
                  >
                    <option value="">
                      { t('position_filter_all', { defaultValue: 'All positions' }) }
                    </option>
                    {
                      positionOptions.map((positionOption) => (
                        <option key={ positionOption } value={ positionOption }>
                          { positionOption }
                        </option>
                      ))
                    }
                  </select>
                </label>
              </div>

              <div className={ styles.gallery__toolbarMeta }>
                <div className={ styles.gallery__stats }>
                  <span>{ organizers.length } { organizersLabel }</span>
                  <span className={ styles.gallery__statsDivider } aria-hidden={ true } />
                  <span>{ visibleTrophies.length } { itemsLabel }</span>
                </div>

                <button
                  type="button"
                  className={ styles.gallery__sortButton }
                  onClick={ () => setSortMode((currentSortMode) => {
                    return currentSortMode === 'recent' ? 'competition' : 'recent'
                  }) }
                  aria-label={ sortLabel }
                >
                  <FontAwesomeIcon
                    icon={ faArrowDownWideShort }
                    className={ styles.gallery__sortButtonIcon }
                  />

                  <span className={ styles.gallery__sortButtonLabel }>
                    { sortLabel }
                  </span>

                  <FontAwesomeIcon
                    icon={ faChevronDown }
                    className={ styles.gallery__sortButtonChevron }
                  />
                </button>
              </div>

              <div
                className={ styles.gallery__memoriesFilters }
                role="group"
                aria-label={ t('memories_filter_label', { defaultValue: 'Filter by memories' }) }
              >
                {
                  galleryMemoriesFilterOptions.map((option) => (
                    <button
                      key={ option.value }
                      type="button"
                      className={ `
                        ${styles.gallery__memoriesFilter}
                        ${activeMemoriesFilter === option.value ? styles.gallery__memoriesFilter_active : ''}
                      ` }
                      aria-pressed={ activeMemoriesFilter === option.value }
                      onClick={ () => void handleMemoriesFilterChange(option.value) }
                    >
                      {
                        t(option.labelKey, {
                          defaultValue: option.value === 0
                            ? 'All'
                            : option.value === 1
                              ? 'With memories'
                              : '2+ memories',
                        })
                      }
                    </button>
                  ))
                }
              </div>
            </div>
          : <OrganizerFilter
              organizers={ organizers }
              organizerId={ organizerId }
              setOrganizerId={ handleOrganizerFilterChange }
              appearance={ appearance }
            />
      }

      { showTrophiesTitle && (
        <h2 className={ styles.gallery__trophiesTitle }>
          { t('trophies_title') }
        </h2>
      ) }

      {
        requestError !== null && (
          <div className={ styles.gallery__feedbackState } role="alert">
            <div>
              <p className={ styles.gallery__feedbackStateTitle }>
                { t('error_title', { defaultValue: 'We could not load the gallery' }) }
              </p>
              <p className={ styles.gallery__feedbackStateDescription }>
                { t(
                  'error_description',
                  { defaultValue: 'Check your connection and try again.' }
                ) }
              </p>
            </div>

            <button
              type="button"
              className={ styles.gallery__feedbackStateButton }
              onClick={ () => void retryFailedRequest() }
              disabled={ loadingTrophies }
            >
              { t('retry_button', { defaultValue: 'Retry' }) }
            </button>
          </div>
        )
      }

      {
        showGalleryContent && isMuseumHome && visibleTrophies.length === 0
          ? <div className={ styles.gallery__emptyState }>
              <p className={ styles.gallery__emptyStateTitle }>
                { emptyState.title }
              </p>
              <p className={ styles.gallery__emptyStateDescription }>
                { emptyState.description }
              </p>

              {
                showEmptyStateActions && (
                  <div className={ styles.gallery__emptyStateActions }>
                    {
                      activeMemoriesFilter === 2 && (
                        <button
                          type="button"
                          className={ styles.gallery__feedbackStateButton }
                          onClick={ () => void handleMemoriesFilterChange(1) }
                          disabled={ loadingTrophies }
                        >
                          { t('empty_action_with_memories', { defaultValue: 'View with memories' }) }
                        </button>
                      )
                    }

                    <button
                      type="button"
                      className={ styles.gallery__emptyStateSecondaryButton }
                      onClick={ () => void handleMemoriesFilterChange(0) }
                      disabled={ loadingTrophies }
                    >
                      { t('empty_action_all', { defaultValue: 'View all' }) }
                    </button>
                  </div>
                )
              }
            </div>
          : showGalleryContent && <TrophyGallery trophies={ visibleTrophies } appearance={ trophyAppearance } />
      }

      {
        showGalleryContent && showLoadMoreButton && (
          <button
            type="button"
            className={ `
              ${styles.gallery__loadMoreButton}
              ${isMuseumHome ? styles.gallery__loadMoreButton_museumHome : ''}
            ` }
            onClick={ loadMoreTrophies }
            disabled={ loadingTrophies }
          >
            { t('load_more_button', { defaultValue: 'Load more' }) }
          </button>
        )
      }
    </section>
  )
}
